// src/auth/LoginUser.js

import React, { useState } from "react";
import { useAuth } from "../app/context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import googleIcon from "../images/Google_Icons-09-512.webp";


/**
 * LoginUser component
 *
 * Renders the login form for users to authenticate via:
 * - Email/password
 * - Google Sign-In (OAuth)
 *
 * Includes CAPTCHA validation, basic brute-force protection (lockout after 5 failed attempts),
 * and automatic redirection after login based on an optional `?redirect=` query param.
 *
 * Uses:
 * - Firebase Auth via custom `useAuth()` context
 * - `react-google-recaptcha` for spam/bot prevention
 * - `react-router-dom` for navigation and linking
 */
export const LoginUser = () => {
  const { login, googleSignIn } = useAuth(); // Auth functions from context
  const navigate = useNavigate();
  const location = useLocation();

  // Form input state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Error and UI state
  const [errorMessage, setErrorMessage] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);

  // Lockout state (after 5 failed attempts)
  const [attemptCount, setAttemptCount] = useState(0);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(0);

  /**
   * Handles the login form submission for email/password authentication.
   * - Validates CAPTCHA
   * - Checks lockout status
   * - Handles redirect to `?redirect=` query param if present
   * - Displays error messages based on auth failure or lockout
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setErrorMessage("Please complete the CAPTCHA to continue.");
      return;
    }

    if (isLockedOut) {
      setErrorMessage(`Too many attempts. Try again in ${lockoutTimer}s.`);
      return;
    }

    // Extract ?redirect=/some/path from URL query
    const searchParams = new URLSearchParams(location.search);
    const redirectPath = searchParams.get("redirect") || "/";

    try {
      await login({ email, password }); // Firebase email/password login
      navigate(redirectPath); // Redirect on success
    } catch (error) {
      console.error("Login error:", error);

      // Increment attempt count and check for lockout
      const newCount = attemptCount + 1;
      setAttemptCount(newCount);

      if (newCount >= 5) {
        setIsLockedOut(true);
        setErrorMessage("Too many failed attempts. Please wait 30 seconds.");
        setLockoutTimer(30);

        // Countdown timer to unlock login
        const interval = setInterval(() => {
          setLockoutTimer((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setIsLockedOut(false);
              setAttemptCount(0);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setErrorMessage("Invalid email or password.");
      }
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-base-200 px-4 py-12">
      <form
        data-testid="login-form"
        onSubmit={handleSubmit}
        className="card bg-base-100 shadow-xl w-full max-w-md p-6"
      >
        <h2 className="text-2xl font-bold text-center mb-6">Login</h2>

        {errorMessage && (
          <div className="text-red-600 mb-4 text-center">
            {errorMessage}
            <div className="mt-2">
              <span className="text-sm">Don’t have an account? </span>
              <Link to="/register" className="text-blue-600 underline">
                Register
              </Link>
            </div>
          </div>
        )}

        <input
          type="email"
          placeholder="Email"
          value={email}
          className="input input-bordered w-full mb-4"
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          className="input input-bordered w-full mb-6"
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <ReCAPTCHA
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={(token) => setCaptchaToken(token)}
          className="mb-4"
        />

        <button
          type="submit"
          className="btn btn-primary btn-block text-base mb-4"
        >
          Login
        </button>

        <button
          type="button"
          onClick={async () => {
            try {
              const searchParams = new URLSearchParams(location.search);
              const redirectPath = searchParams.get("redirect") || "/";
              await googleSignIn();
              navigate(redirectPath);
            } catch (err) {
              console.error("Google Sign-In failed:", err);
              setErrorMessage("Google Sign-In failed. Please try again.");
            }
          }}
          className="btn btn-outline btn-block mb-4 flex items-center justify-between"
        >
          <img src={googleIcon} alt="Google logo" className="w-5 h-5" />
          <span className="flex-1 text-center">Login with Google</span>
          <img src={googleIcon} alt="Google logo" className="w-5 h-5" />
        </button>

        <Link to="/" className="btn btn-outline btn-block">
          ← Back to Home
        </Link>
      </form>
    </div>
  );
};